import { Injectable, signal } from '@angular/core';

export interface HeaderButton {
  label: string;
  icon?: string;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'danger' | 'help' | 'contrast';
  outlined?: boolean;
  disabled?: boolean;
  loading?: boolean;
  action: () => void;
  type?: 'button' | 'submit';
}

export type SaveStatus = 'unsaved' | 'saving' | 'saved';

export interface SaveIndicator {
  status: SaveStatus;
  lastSavedAt?: Date;
}

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  // Signals for reactive state management
  public readonly title = signal<string>('');
  public readonly subtitle = signal<string>('');
  public readonly buttons = signal<HeaderButton[]>([]);
  public readonly loading = signal<boolean>(false);
  public readonly saveIndicator = signal<SaveIndicator | null>(null);

  /**
   * Set the header title
   */
  setTitle(title: string): void {
    this.title.set(title);
  }

  /**
   * Set the header subtitle
   */
  setSubtitle(subtitle: string): void {
    this.subtitle.set(subtitle);
  }

  /**
   * Set the action buttons in the header
   */
  setButtons(buttons: HeaderButton[]): void {
    this.buttons.set(buttons);
  }

  /**
   * Set the loading state (shows skeletons when true)
   */
  setLoading(loading: boolean): void {
    this.loading.set(loading);
  }

  /**
   * Set the save indicator
   */
  setSaveIndicator(indicator: SaveIndicator | null): void {
    this.saveIndicator.set(indicator);
  }

  /**
   * Clear all header content (title, subtitle, and buttons)
   */
  clear(): void {
    this.title.set('');
    this.subtitle.set('');
    this.buttons.set([]);
    this.loading.set(false);
    this.saveIndicator.set(null);
  }

  /**
   * Update a specific button's state (useful for loading states)
   */
  updateButton(index: number, updates: Partial<HeaderButton>): void {
    const currentButtons = this.buttons();
    if (index >= 0 && index < currentButtons.length) {
      const updatedButtons = [...currentButtons];
      updatedButtons[index] = { ...updatedButtons[index], ...updates };
      this.buttons.set(updatedButtons);
    }
  }
}
